import { clsx } from 'clsx';
import { useRefresh } from 'react-admin';
import { Close } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Form, message, Modal, Input, Select, SelectProps } from 'antd';

import { useRequest } from '@hooks';
import { ticketHttp } from '@network';
import { useSettingsContext } from '@providers';
import { SupportFiles } from '@pages/support/components';
import { SupportCreateTicket, SupportDocument, SupportPlatform } from '@types';
import { BlueOutlineButton, ConfirmDelete, EditorInput, OrangeOutlineButton } from '@components';
import cls from './ToolbarTicket.module.css';
import { Editor } from '@tinymce/tinymce-react';

const platformsOptions: { value: SupportPlatform; label: SupportPlatform }[] = [
  { value: 'Client', label: 'Client' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Partners', label: 'Partners' },
];

const initialForm: SupportCreateTicket = {
  platform: platformsOptions[1].value,
  issue: '',
  fileIds: [],
  links: [],
};

export const ToolbarTicket = () => {
  const location = useLocation();
  const { ticketOpened, setTicketOpened } = useSettingsContext();

  useEffect(() => {
    setTicketOpened(false);
  }, [location.pathname]);

  return (
    <>
      <div className={cls.icon} onClick={() => setTicketOpened(true)}>
        <TicketIcon />
      </div>

      <Modal
        title="I have a tech issue ¡Help!"
        destroyOnClose
        maskClosable={false}
        open={ticketOpened}
        footer={false}
        onCancel={() => setTicketOpened(false)}
        closable={false}
        className={cls.modal}
        width={800}
        closeIcon={<Close className={cls.closeIcon} />}
      >
        {ticketOpened && <TicketForm />}
      </Modal>
    </>
  );
};

const TicketForm = () => {
  const MAX_ISSUE_LENGTH = 1000;
  const refresh = useRefresh();
  const { setTicketOpened } = useSettingsContext();
  const [form] = Form.useForm<SupportCreateTicket>();
  const [documents, setDocuments] = useState<SupportDocument[]>([]);
  const [fileIds, setFileIds] = useState<number[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [issue, setIssue] = useState<string>('');
  const options: SelectProps['options'] = [];

  const onClose = () => {
    setTicketOpened(false);
    form.resetFields();
    setDocuments([]);
    setFileIds([]);
    setIssue('');
  };

  const onCancel = () => {
    onClose();
  };

  const onIssueSet = (value: string) => {
    if (value === '' || value?.trim().length <= MAX_ISSUE_LENGTH) {
      setIssue(value?.trim());
    }
  };

  const { submit, loading } = useRequest(async () => {
    try {
      const fields = await form.validateFields();
      await ticketHttp.createTicket({ ...fields, issue, fileIds, links });
      message.success('Tech issue is successfully requested!', 2);
      onClose();
      await ticketHttp.getTicketList();
      refresh();
    } catch (e: any) {
      console.error(e);
      message.error('The form is not valid. Please check for errors');
    }
  });

  useEffect(() => {
    console.log(issue);
  }, [issue]);

  return (
    <Form
      form={form}
      initialValues={initialForm}
    >
      <Form.Item
        name="platform"
        label={false}
        className={clsx(cls.field, cls.fieldSelect)}
        rules={[{ required: true, message: '' }]}
      >
        <div className={cls.row}>
          <div className={clsx(cls.col, cls.labelCol)}>Platform *</div>
          <div className={clsx(cls.col, cls.fieldCol)}>
            <Select
              bordered={false}
              style={{ width: '100%' }}
              options={platformsOptions}
              onSelect={(e) => form.setFieldValue('platform', e)}
              defaultValue={platformsOptions[1].value}
            />
          </div>
        </div>
      </Form.Item>
      <div className={cls.row}>
        <div className={cls.labelFull}>Describe your issue *</div>
        <div className={cls.centered}>
          <Editor
            value={issue}
            onEditorChange={onIssueSet}
            apiKey="oph1re1rbxqlrcim9vh1z8z42fo2mtx7jvvbkuupzh9nn5q6"
            init={{
              height: 300,
              menubar: false,
              plugins: 'advlist autolink lists link image charmap table code help',
              toolbar_mode: 'wrap',
              toolbar: 'undo redo formatselect '
                + 'bold italic backcolor | bullist numlist outdent indent table '
                + 'removeformat code help | '
                + 'alignleft aligncenter alignright alignjustify ',
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
            }}
          />
        </div>
      </div>

      <div className={cls.row}>
        <div className={cls.labelFull}>Upload Screenshot or evidence</div>
        <div className={cls.centered}>
          <SupportFiles
            files={documents}
            setFiles={setFileIds}
            setDocuments={setDocuments}
            className={cls.files}
            maxFiles={3}
          />
        </div>
      </div>
      <Form.Item
        name="links"
        label={false}
        rules={[
          { required: true, message: '' },
          { max: 500, message: 'Text should be less then 500 characters' },
        ]}
      >
        <div className={cls.row}>
          <div className={cls.labelFull}>And/or add the URL’s *</div>
          <Select
            mode="tags"
            style={{ width: '100%' }}
            onChange={(value: string[]) => setLinks(value)}
            options={options}
          />
        </div>
      </Form.Item>

      <div className={cls.controls}>
        <ConfirmDelete cb={onCancel} title={'Are you sure?'} overlayClassName={cls.confirm} disabled={!issue && !links.length && !fileIds.length}>
          <OrangeOutlineButton
            disabled={loading}
            className={cls.btn}
            onClick={(issue || links.length || fileIds.length) ? undefined : onCancel}
          >Cancel</OrangeOutlineButton>
        </ConfirmDelete>
        <BlueOutlineButton
          disabled={loading}
          className={cls.btn}
          onClick={submit}
        >Send</BlueOutlineButton>
      </div>
    </Form>
  );
};

const TicketIcon = () => (
  <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill="white" fillRule="evenodd" clipRule="evenodd" d="M0 5.98193C0.832227 5.99368 1.50325 6.67198 1.50325 7.507C1.50325 8.34202 0.832227 9.02031 0 9.03206V5.98193ZM0.00975825 0.859745H0.761851C1.03799 0.859745 1.26185 1.0836 1.26185 1.35974V2.73974C1.26185 2.9614 1.4089 3.15397 1.61565 3.23387C1.71588 3.2726 1.81441 3.31474 1.91111 3.36014C2.11578 3.45626 2.36193 3.42624 2.52182 3.26635L3.50095 2.28722C3.69621 2.09196 4.01279 2.09196 4.20805 2.28722L5.34482 3.42399C5.54008 3.61925 5.54008 3.93583 5.34482 4.13109L4.30914 5.16677C4.15546 5.32046 4.12127 5.55481 4.20501 5.75537C4.23859 5.83581 4.26995 5.9174 4.29902 6.00007C4.37461 6.21505 4.57094 6.37021 4.79883 6.37021H6.27232C6.54846 6.37021 6.77232 6.59407 6.77232 6.87021V8.47785C6.77232 8.75399 6.54846 8.97785 6.27232 8.97785H4.6854C4.47 8.97785 4.28133 9.11694 4.1975 9.31536C4.17414 9.37064 4.14974 9.42536 4.12431 9.47951C4.0282 9.68418 4.05822 9.93034 4.21811 10.0902L5.34485 11.217C5.54011 11.4122 5.54011 11.7288 5.34485 11.9241L4.20808 13.0608C4.01282 13.2561 3.69623 13.2561 3.50097 13.0608L2.31768 11.8775C2.16399 11.7239 1.92964 11.6897 1.72907 11.7734C1.69152 11.7891 1.65371 11.8043 1.61565 11.819C1.4089 11.8989 1.26185 12.0915 1.26185 12.3131V13.9883C1.26185 14.2645 1.03799 14.4883 0.761852 14.4883H0.0268538C0.131179 14.9291 0.527285 15.2571 1 15.2571H14.1329L21 8.39003V1C21 0.447715 20.5523 0 20 0H1C0.495317 0 0.0779544 0.373863 0.00975825 0.859745ZM13.235 10.1083C13.5991 10.1083 13.8904 10.4146 13.8904 10.7975C13.8904 11.1805 13.5991 11.4868 13.235 11.4868C12.8709 11.4868 12.5796 11.1805 12.5796 10.7975C12.5796 10.4146 12.8709 10.1083 13.235 10.1083ZM13.235 2.5725C14.6768 2.5725 15.842 3.79783 15.842 5.31418L15.8565 5.34481C15.8565 6.61609 15.0264 7.68826 13.9049 7.99459V8.76042C13.9049 9.14334 13.6136 9.44967 13.2495 9.44967C12.8854 9.44967 12.5941 9.14334 12.5941 8.76042V7.38193C12.5941 6.99901 12.8854 6.69268 13.2495 6.69268C13.9777 6.69268 14.5603 6.08001 14.5603 5.31418C14.5603 4.54835 13.9777 3.93568 13.2495 3.93568C12.5213 3.93568 11.9388 4.56366 11.9388 5.31418C11.9388 5.69709 11.6475 6.00343 11.2834 6.00343C10.9193 6.00343 10.628 5.69709 10.628 5.31418C10.628 3.79783 11.7931 2.5725 13.235 2.5725Z" />
  </svg>
);
